// ===============================================================
// ✅ DeleteAccountPage.js — Request account deletion (Server-only)
// ===============================================================

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./DeleteAccountPage.css";

import TopBar from "../components/common/TopBar";
import ConfirmDeletePopup from "../components/ConfirmDeletePopup";
import LivoraToast from "../components/common/LivoraToast";
import useLivoraToast from "../hooks/useLivoraToast";
import { useTranslation } from "react-i18next";

// 🔥 Firebase
import {
  auth,
  googleProvider,
  appleProvider,
  functions,
  httpsCallable,
} from "../firebase";

import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
} from "firebase/auth";

function DeleteAccountPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // ✅ Livora Toast
  const { toast, showToast, hideToast } = useLivoraToast();

  // 🧠 State
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [deleteText, setDeleteText] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [providerId, setProviderId] = useState(null);
  const [requiresPassword, setRequiresPassword] = useState(false);
  const [showPopup, setShowPopup] = useState(false);

  // -------------------------------------------------------------
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) {
      navigate("/");
      return;
    }

    setEmail(user.email || "");

    const firstProvider = user.providerData?.[0];
    const pid = firstProvider?.providerId || null;

    setProviderId(pid);
    setRequiresPassword(pid === "password");
  }, [navigate]);

  // -------------------------------------------------------------
  const reauthenticateUser = async () => {
    try {
      const user = auth.currentUser;
      if (!user) return false;

      if (requiresPassword) {
        if (!password.trim()) {
          showToast(
            "error",
            t("enter_password_confirm", {
              defaultValue: "أدخل كلمة المرور للتأكيد",
            })
          );
          return false;
        }

        if (!user.email) {
          showToast(
            "error",
            t("reauth_failed", {
              defaultValue: "فشلت إعادة التحقق",
            })
          );
          return false;
        }

        const credential = EmailAuthProvider.credential(
          user.email,
          password
        );

        await reauthenticateWithCredential(user, credential);
        return true;
      }

      let provider = null;
      if (providerId === "google.com") provider = googleProvider;
      else if (providerId === "apple.com") provider = appleProvider;

      if (provider) {
        await reauthenticateWithPopup(user, provider);
      }

      return true;
    } catch (error) {
      console.error("Reauth error:", error);

      if (
        error.code === "auth/wrong-password" ||
        error.code === "auth/invalid-credential"
      ) {
        showToast(
          "error",
          t("wrong_password", {
            defaultValue: "كلمة المرور غير صحيحة",
          })
        );
      } else if (error.code === "auth/popup-closed-by-user") {
        showToast(
          "error",
          t("reauth_cancelled", {
            defaultValue: "تم إغلاق نافذة التحقق قبل الإكمال",
          })
        );
      } else {
        showToast(
          "error",
          t("reauth_failed", {
            defaultValue: "فشلت إعادة التحقق",
          })
        );
      }

      return false;
    }
  };

  // -------------------------------------------------------------
  const handleDeleteRequest = async () => {
    const user = auth.currentUser;
    if (!user) return;

    if (deleteText.trim().toLowerCase() !== "delete") {
      showToast(
        "error",
        t("type_delete_word", {
          defaultValue: "يجب كتابة كلمة delete للتأكيد",
        })
      );
      return;
    }

    setIsLoading(true);

    try {
      const ok = await reauthenticateUser();
      if (!ok) {
        setIsLoading(false);
        return;
      }

      const requestAccountDeletion = httpsCallable(
        functions,
        "requestAccountDeletion"
      );

      await requestAccountDeletion({
        reason: "User requested deletion from settings",
      });

      showToast(
        "success",
        t("delete_request_sent", {
          defaultValue: "تم إرسال طلب حذف الحساب",
        })
      );

      setShowPopup(false);
      setPassword("");
      setDeleteText("");

      setTimeout(async () => {
        await auth.signOut();
        navigate("/pending-delete");
      }, 700);
    } catch (error) {
      console.error("Delete request error:", error);

      const code = error?.code || error?.message || "";

      if (
        code.includes("failed-precondition") ||
        code.includes("يوجد طلب حذف مفعّل مسبقًا")
      ) {
        showToast(
          "error",
          t("delete_already_requested", {
            defaultValue: "يوجد طلب حذف مفعّل مسبقًا",
          })
        );
      } else if (
        code.includes("unauthenticated") ||
        code.includes("يجب تسجيل الدخول")
      ) {
        showToast(
          "error",
          t("login_required", {
            defaultValue: "يجب تسجيل الدخول أولًا",
          })
        );
      } else {
        showToast(
          "error",
          t("general_error", {
            defaultValue: "حدث خطأ عام",
          })
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  // =============================================================
  return (
    <div className="delete-page">
      {/* ✅ Card / Popup */}
      {showPopup && (
        <ConfirmDeletePopup
          onCancel={() => setShowPopup(false)}
          onConfirm={handleDeleteRequest}
        />
      )}

      <TopBar
        title={t("delete_account", { defaultValue: "حذف الحساب" })}
        onBack={() => navigate(-1)}
      />

      <div className="delete-content">
        <form autoComplete="off">
          <div className="warning-box">
            <p>
              {t("delete_warning_7_days", {
                defaultValue: "سيتم جدولة حذف الحساب بعد 7 أيام.",
              })}
            </p>
            <p className="danger-text">
              {t("delete_warning_final", {
                defaultValue: "بعد تنفيذ الحذف قد لا يمكن استعادة البيانات.",
              })}
            </p>
          </div>

          <div className="field-wrapper">
            <label className="field-label">
              {t("email", { defaultValue: "البريد الإلكتروني" })}
            </label>
            <input
              type="email"
              className="input-field"
              value={email}
              disabled
              readOnly
              autoComplete="off"
            />
          </div>

          {requiresPassword && (
            <div className="field-wrapper">
              <label className="field-label">
                {t("current_password", {
                  defaultValue: "كلمة المرور الحالية",
                })}
              </label>
              <input
                type="password"
                className="input-field"
                placeholder={t("enter_password_placeholder", {
                  defaultValue: "أدخل كلمة المرور الحالية",
                })}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="off"
              />
            </div>
          )}

          <div className="field-wrapper">
            <label className="field-label">
              <span>
                {t("type_delete_label", {
                  defaultValue: "اكتب الكلمة التالية للتأكيد",
                })}
              </span>
              <span className="delete-word">delete</span>
            </label>
            <input
              type="text"
              className="input-field"
              placeholder={t("type_delete_placeholder", {
                defaultValue: "اكتب delete هنا",
              })}
              value={deleteText}
              onChange={(e) => setDeleteText(e.target.value)}
              autoComplete="off"
            />
          </div>

          <button
            type="button"
            className="delete-main-btn"
            onClick={() => setShowPopup(true)}
            disabled={isLoading}
          >
            {isLoading
              ? t("sending_request", { defaultValue: "جارٍ إرسال الطلب..." })
              : t("delete_request_button", {
                  defaultValue: "طلب حذف الحساب",
                })}
          </button>
        </form>
      </div>

      {/* ✅ Toast */}
      <LivoraToast toast={toast} onClose={hideToast} />
    </div>
  );
}

export default DeleteAccountPage;
